import React, { memo, useMemo } from 'react';
import styled from 'styled-components';
import {
	CREATE_TEMPLATE_FORM_FIELDS,
	useCreatePreFillableFormController,
} from '@dealstack/frontend/admin/pages/Manage/ManagePreFillableForms/CreatePreFillableForm/useCreatePreFillableFormController.ts';
import { GODMODE_ROOT_ROUTES } from '@dealstack/frontend/admin/pages/Routing/Routing.tsx';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { Button, Divider, Flex, Text } from '@mantine/core';
import {
	ACCEPT_FILES_TYPES,
	DropzoneV2,
} from '@dealstack/frontend/shared/components/DropzoneV2/DropzoneV2';
import { FormProvider } from 'react-hook-form';
import { MultiSelect, Select, Textarea, TextInput } from 'react-hook-form-mantine';
import { PRE_FILLABLE_FORM_TEMPLATE_TYPE_OPTIONS } from '@dealstack/shared/lib/common/business/preFillableForms.ts';
import { DocumentViewer } from '@dealstack/frontend/shared/components/DocumentViewer/DocumentViewer';
import { PdfTemplateView } from '@dealstack/frontend/admin/pages/Manage/ManagePreFillableForms/common/PdfTemplateView/PdfTemplateView.tsx';
import { ManageCustomDetailsDefinitionsModal } from '@dealstack/frontend/admin/pages/Manage/ManagePreFillableForms/CreatePreFillableForm/ManageCustomDetailsDefinitionsModal/ManageCustomDetailsDefinitionsModal.tsx';
import { mapCustomDetailsDefinitionToSource } from '@dealstack/frontend/admin/pages/Manage/common/EditFieldMappingModal/availableSources.ts';
import { categoryOptions } from '@dealstack/frontend/shared/utils/customDetailCategoryOptions.ts';

interface Props {
	className?: string;
}

const CreatePreFillableForm = ({ className }: Props) => {
	const {
		navigate,
		onFileDrop,
		isFileUploading,
		showForm,
		uploadedFileName,
		createPreFillableFormTemplateForm,
		assignedProjectsOptions,
		assignedOrganizationsOptions,
		onSubmit,
		isFormSubmitting,
		fileBlob,
		showManageCustomDetailsDefinitionsModal,
		setShowManageCustomDetailsDefinitionsModal,
	} = useCreatePreFillableFormController();

	const customDetailsDefinitionsSources = useMemo(() => {
		return createPreFillableFormTemplateForm
			.getValues(CREATE_TEMPLATE_FORM_FIELDS.CUSTOM_DETAIL_DEFINITIONS)
			.map(mapCustomDetailsDefinitionToSource);
	}, [
		createPreFillableFormTemplateForm.watch(
			CREATE_TEMPLATE_FORM_FIELDS.CUSTOM_DETAIL_DEFINITIONS
		),
	]);

	return (
		<div className={className}>
			<Button
				size="sm"
				variant="outline"
				onClick={() => navigate(GODMODE_ROOT_ROUTES.MANAGE_PRE_FILLABLE_FORMS)}
				leftSection={<Icon variant="arrow-left" />}
			>
				Back to table
			</Button>
			<FormProvider {...createPreFillableFormTemplateForm}>
				<Flex direction="column" gap="1rem" mt="1rem">
					<Flex gap="2rem" align="center" justify="space-between" mt="1rem">
						<Text size="B4Bold">Add new pre-fillable form template</Text>
						{showForm && (
							<Button
								size="md"
								variant="primary"
								onClick={createPreFillableFormTemplateForm.handleSubmit(onSubmit)}
								disabled={isFormSubmitting}
							>
								Create
							</Button>
						)}
					</Flex>

					<Divider />

					{!showForm && (
						<Box>
							<Text size="B5Bold">Upload pdf form template file</Text>
							<DropzoneV2
								onDropFile={onFileDrop}
								accept={ACCEPT_FILES_TYPES.pdf}
								multiple={false}
								title="Upload pdf form template"
								size="STD"
								loading={isFileUploading}
							/>
						</Box>
					)}

					{showForm && (
						<>
							<Box>
								<Text size="B5Bold">Selected file: {uploadedFileName}</Text>
								<Flex direction="column" gap="1rem">
									<Flex gap="1rem" w="100%">
										<TextInput
											name={CREATE_TEMPLATE_FORM_FIELDS.NAME}
											control={createPreFillableFormTemplateForm.control}
											label="Template name"
											placeholder="Enter template name"
											size="md"
											w="100%"
										/>
										<Select
											name={CREATE_TEMPLATE_FORM_FIELDS.TYPE}
											control={createPreFillableFormTemplateForm.control}
											data={PRE_FILLABLE_FORM_TEMPLATE_TYPE_OPTIONS}
											label="Template type"
											placeholder="Select template type"
											size="md"
											w="100%"
										/>
									</Flex>
									<Flex gap="1rem" w="100%">
										<MultiSelect
											name={CREATE_TEMPLATE_FORM_FIELDS.ASSIGNED_PROJECTS}
											control={createPreFillableFormTemplateForm.control}
											data={assignedProjectsOptions}
											label="Assigned deals"
											placeholder="Select deals you want to assign with template"
											size="md"
											w="100%"
											searchable
											clearable
											sx={(theme) => ({
												value: { backgroundColor: theme.colors.blue[4] },
											})}
										/>
										<Textarea
											name={CREATE_TEMPLATE_FORM_FIELDS.INSTRUCTION}
											control={createPreFillableFormTemplateForm.control}
											label="Instruction"
											size="md"
											placeholder="Instruction to the investors"
											w="100%"
										/>
									</Flex>

									<Flex gap="1rem" w="100%">
										<MultiSelect
											name={
												CREATE_TEMPLATE_FORM_FIELDS.ASSIGNED_ORGANIZATIONS
											}
											control={createPreFillableFormTemplateForm.control}
											data={assignedOrganizationsOptions}
											label="Assigned organisations"
											placeholder="New programmes of these organisations get the form"
											size="md"
											w="100%"
											searchable
											clearable
											sx={(theme) => ({
												value: { backgroundColor: theme.colors.blue[4] },
											})}
										/>
										<Select
											name={CREATE_TEMPLATE_FORM_FIELDS.CATEGORY}
											control={createPreFillableFormTemplateForm.control}
											data={categoryOptions}
											label="Template category"
											placeholder="Select template category"
											size="md"
											w="100%"
											clearable
										/>
									</Flex>
								</Flex>
							</Box>

							<Box>
								<Flex justify="space-between">
									<Text size="B5Medium" mb="1rem">
										Uploaded file: {uploadedFileName}
									</Text>

									<Button
										size="sm"
										h="2rem"
										variant="primary"
										onClick={() => {
											setShowManageCustomDetailsDefinitionsModal(true);
										}}
									>
										Manage custom details definitions
									</Button>
								</Flex>

								{!!fileBlob && (
									<PdfTemplateView
										fileBlob={fileBlob}
										fillableFields={createPreFillableFormTemplateForm.watch(
											CREATE_TEMPLATE_FORM_FIELDS.FILLABLE_FIELDS_MAPPING
										)}
										customDetailsDefinitionsSources={
											customDetailsDefinitionsSources
										}
									/>
								)}
							</Box>
						</>
					)}
				</Flex>

				{showManageCustomDetailsDefinitionsModal && (
					<ManageCustomDetailsDefinitionsModal
						opened={showManageCustomDetailsDefinitionsModal}
						onClose={() => setShowManageCustomDetailsDefinitionsModal(false)}
					/>
				)}
			</FormProvider>
		</div>
	);
};

const Box = styled.div`
	display: flex;
	flex-direction: column;
	gap: 1rem;
	padding: 1rem;
	width: 100%;
	border-radius: 0.5rem;
	background-color: ${(props) => props.theme.v3.colors.gray[2]};
`;

const Memoized = memo(CreatePreFillableForm);
const Styled = styled(Memoized)`
	.mantine-MultiSelect-value {
		background-color: ${(props) => props.theme.v3.colors.blue[2]};
	}

	${DocumentViewer} {
		input.highlighted-pdf-input-valid {
			border: 1px solid ${(props) => props.theme.v3.colors.green[8]} !important;
		}
		input.highlighted-pdf-input-invalid {
			border: 1px solid ${(props) => props.theme.v3.colors.red[6]} !important;
		}
	}
`;

export { Styled as CreatePreFillableForm };
